import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dimensions, Linking, Platform, StyleSheet, View } from 'react-native';
import { BottomNavigation } from '../components/BottomNavigation';
import { HamburgerButton } from '../components/HamburgerButton';
import { Sidebar, SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from '../components/Sidebar';
import { TopBarProfile } from '../components/TopBarProfile';
import authService from '../services/authService';
import analyticsService from '../services/analyticsService';
import { apiService } from '../services/apiService';
import { addAuthErrorListener } from '../utils/authInterceptor';
import { isXl } from '../utils/responsiveStyles';
import { EventsScreen } from './EventsScreen';
import { ForgotPasswordScreen } from './ForgotPasswordScreen';
import { HomeScreen } from './HomeScreen';
import { LoginScreen } from './LoginScreen';
import { ProductDetailScreen } from './ProductDetailScreen';
import { ProductsScreen } from './ProductsScreen';
import { ResetPasswordScreen } from './ResetPasswordScreen';
import { SplashScreen } from './SplashScreen';
import { UserAnalyticsAdminScreen } from './UserAnalyticsAdminScreen';
import { getAppearanceTokens, type AppTheme } from '../theme/appearanceTokens';
import { SettingsScreen, type AppLanguage } from './SettingsScreen';
import { UsersManagementScreen } from './UsersManagementScreen';
import { WelcomeScreen } from './WelcomeScreen';
import { ImpersonationBar } from '../components/ImpersonationBar';
import { SharedProductScreen } from './SharedProductScreen';
import { NotificationsScreen } from './NotificationsScreen';
import type { InAppNotification, NotificationPreferences } from '../types';
import { getInitialShareToken, parseShareTokenFromUrl } from '../utils/shareRoute';

const { width } = Dimensions.get('window');
const isDesktop = width >= 1024; // Desktop only, not tablet
const isLargeDesktop = width >= 1280; // Large desktop (sidebar expanded by default)
const isSmallDesktop = width >= 1024 && width < 1280; // Small laptop (sidebar collapsed by default)
const isTablet = isXl(width) && width < 1024; // Tablet: >= 768 and < 1024

const EMPTY_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  inAppEnabled: false,
  emailEnabled: false,
  emailProductStatusEnabled: false,
  emailKeyDatesEnabled: false,
  emailExpiryEnabled: false,
  emailPortfolioEnabled: false,
  emailKeyDatesCouponEnabled: false,
  emailKeyDatesAutocallEnabled: false,
  emailKeyDatesIssuerCallEnabled: false,
  emailBarrierHitEnabled: false,
  emailKeyDatesSameDayEnabled: false,
  emailKeyDates1dEnabled: false,
  emailKeyDates7dEnabled: false,
  emailKeyDatesCustomEnabled: false,
  emailKeyDatesCustomDays: 30,
  productStatusEnabled: false,
  keyDatesEnabled: false,
  keyDatesCouponEnabled: false,
  keyDatesAutocallEnabled: false,
  keyDatesCallWindowEnabled: false,
  keyDatesBarrierEnabled: false,
  keyDatesSameDayEnabled: false,
  keyDates1dEnabled: false,
  keyDates7dEnabled: false,
  keyDatesCustomEnabled: false,
  keyDatesCustomDays: 30,
  expiryEnabled: false,
  portfolioEnabled: false,
  expirySameDayEnabled: false,
  expiry1dEnabled: false,
  expiry7dEnabled: false,
  expiryCustomEnabled: false,
  expiryCustomDays: 30,
  cleanupReadEnabled: true,
  cleanupUnreadEnabled: false,
  cleanupRetentionDays: 14,
};

// SIDEBAR_WIDTH y SIDEBAR_COLLAPSED_WIDTH se importan del Sidebar

type AppScreen =
  | 'splash'
  | 'welcome'
  | 'login'
  | 'forgot-password'
  | 'reset-password'
  | 'main'
  | 'products'
  | 'product-detail'
  | 'events'
  | 'analytics'
  | 'users'
  | 'settings'
  | 'notifications'
  | 'shared-product';

interface NavbarUserData {
  readonly name: string;
  readonly email: string;
  readonly avatarUrl: string | null;
}

export const AppNavigator: React.FC = () => {
  const [currentScreen, setCurrentScreen] = useState<AppScreen>('splash');
  const [isInitialized, setIsInitialized] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [selectedProductIsin, setSelectedProductIsin] = useState<string | null>(null);
  const [productDetailReturnScreen, setProductDetailReturnScreen] = useState<
    'main' | 'products' | 'events'
  >('products');
  const [productDetailFromReport, setProductDetailFromReport] = useState(false);
  const [productFilters, setProductFilters] = useState<{
    currency?: string;
    issuer?: string;
    maturityQuarter?: string;
    underlying?: string;
    eventType?: string;
    weekStart?: string;
    eventDate?: string;
    eventBucket?: 'nextWeek' | 'nextMaturity';
    nearBarrier?: boolean;
    riskType?: 'barrier' | 'barrierHit' | 'lowStrike';
    /** 1–99 = filtrar por distancia %; omitir o 100 = sin tope (solo barrier / strike monitor) */
    maxDistancePct?: number;
    productTypes?: string[];
    issuers?: string[];
    reportIds?: string[];
    selectedProductTypes?: string[];
    selectedIssuers?: string[];
    ownershipScope?: 'group' | 'self';
    includeWarrants?: boolean;
  } | null>(null);
  const [ownershipScope, setOwnershipScope] = useState<'group' | 'self'>('group');
  const [showOwnershipScopeToggle, setShowOwnershipScopeToggle] = useState(true);
  const [ownershipScopeAvailabilityLoaded, setOwnershipScopeAvailabilityLoaded] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop); // Abierto por defecto en desktop
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isSmallDesktop); // Colapsada en laptops pequeñas
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const [navbarUserData, setNavbarUserData] = useState<NavbarUserData>({
    name: 'User',
    email: '',
    avatarUrl: null,
  });
  const [appLanguage, setAppLanguage] = useState<AppLanguage>('en');
  const [appTheme, setAppTheme] = useState<AppTheme>('light');
  const appearance = getAppearanceTokens(appTheme);
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>(
    EMPTY_NOTIFICATION_PREFERENCES
  );

  const loadNotifications = useCallback(async () => {
    try {
      const data = await apiService.getNotifications();
      setNotifications(data);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  }, []);

  const loadNotificationPreferences = useCallback(async () => {
    try {
      const data = await apiService.getNotificationPreferences();
      setNotificationPreferences(data);
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    }
  }, []);

  const unreadNotificationsCount = useMemo(
    () => notifications.filter(notification => !notification.isRead).length,
    [notifications]
  );

  const handleNotificationPreferencesChange = useCallback(
    async (input: Partial<NotificationPreferences>) => {
      try {
        const updated = await apiService.updateNotificationPreferences(input);
        setNotificationPreferences(updated);
        await loadNotifications();
      } catch (error) {
        console.error('Error updating notification preferences:', error);
      }
    },
    [loadNotifications]
  );

  const handleMarkAllNotificationsAsRead = useCallback(async () => {
    const previousNotifications = notifications;
    setNotifications(previousValue =>
      previousValue.map(item => (item.isRead ? item : { ...item, isRead: true }))
    );

    try {
      await apiService.markAllNotificationsAsRead();
    } catch (error) {
      setNotifications(previousNotifications);
      console.error('Error marking all notifications as read:', error);
    } finally {
      await loadNotifications();
    }
  }, [notifications, loadNotifications]);

  const handleNotificationPress = useCallback(
    async (notification: InAppNotification) => {
      if (!notification.isRead) {
        const previousNotifications = notifications;
        setNotifications(previousValue =>
          previousValue.map(item => (item.id === notification.id ? { ...item, isRead: true } : item))
        );

        try {
          await apiService.markNotificationAsRead(notification.id);
        } catch (error) {
          setNotifications(previousNotifications);
          console.error('Error marking notification as read:', error);
        } finally {
          await loadNotifications();
        }
      }

      if (notification.actionType === 'OPEN_PRODUCT_DETAIL' && notification.actionPayload?.isin) {
        setSelectedProductIsin(notification.actionPayload.isin);
        setCurrentScreen('product-detail');
        return;
      }

      if (notification.actionType === 'OPEN_EVENTS') {
        setCurrentScreen('events');
        return;
      }

      if (notification.actionType === 'OPEN_PORTFOLIO') {
        setCurrentScreen('products');
        return;
      }

      setCurrentScreen('main');
    },
    [notifications, loadNotifications]
  );


  const loadUserPreferences = useCallback(async () => {
    try {
      const preferences = await apiService.getUserPreferences();
      // TEMP: client demo scope - keep fixed English/Light.
      // if (preferences.language) {
      //   setAppLanguage(preferences.language);
      // }
      // if (preferences.theme) {
      //   setAppTheme(preferences.theme);
      // }
      setAppLanguage('en');
      setAppTheme('light');
      if (typeof preferences.avatarUrl === 'string') {
        setNavbarUserData(prev => ({ ...prev, avatarUrl: preferences.avatarUrl }));
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  }, []);

  const AUTH_SCREENS_WITHOUT_HEARTBEAT: AppScreen[] = [
    'splash',
    'welcome',
    'login',
    'forgot-password',
    'reset-password',
  ];
  const analyticsHeartbeatActive =
    isInitialized &&
    authService.isAuthenticated() &&
    !authService.getImpersonationChromeState().isImpersonating &&
    !AUTH_SCREENS_WITHOUT_HEARTBEAT.includes(currentScreen);

  useEffect(() => {
    if (!analyticsHeartbeatActive) {
      analyticsService.stopHeartbeat();
      return;
    }

    void analyticsService.startHeartbeat();
    return () => {
      analyticsService.stopHeartbeat();
    };
  }, [analyticsHeartbeatActive]);

  useEffect(() => {
    const init = async () => {
      const initialShareToken = getInitialShareToken();
      if (initialShareToken) {
        setShareToken(initialShareToken);
        setCurrentScreen('shared-product');
        setIsInitialized(true);
        return;
      }

      const hasDeepLink = await handleDeepLink();
      if (!hasDeepLink) {
        await initializeApp();
      }
    };
    init();
  }, []);

  // Separate effect for auth error listener - only register once
  useEffect(() => {
    // Listen for authentication errors and redirect to login
    const removeAuthErrorListener = addAuthErrorListener(() => {
      console.log('🔐 Auth error detected - redirecting to login');
      // Use functional update to get current screen value
      setCurrentScreen(prevScreen => {
        // Only redirect if not already on login/welcome screens
        if (
          prevScreen !== 'login' &&
          prevScreen !== 'welcome' &&
          prevScreen !== 'splash' &&
          prevScreen !== 'shared-product'
        ) {
          return 'login';
        }
        return prevScreen;
      });
    });

    return () => {
      removeAuthErrorListener();
    };
  }, []); // Empty dependency array - only register once

  const handleDeepLink = async (): Promise<boolean> => {
    // Handle deep link when app is already running
    const handleUrl = (url: string): boolean => {
      console.log('Deep link received:', url);

      const sharedToken = parseShareTokenFromUrl(url);
      if (sharedToken) {
        setShareToken(sharedToken);
        setCurrentScreen('shared-product');
        setIsInitialized(true);
        return true;
      }

      // Parse URL - handle both app scheme and web URLs
      let path = '';
      let queryParams: any = {};

      if (url.includes('lifecycle://')) {
        // Mobile deep link - manual parsing since we're using RN Linking
        const urlParts = url.split('://')[1] || '';
        const [pathPart, queryPart] = urlParts.split('?');
        path = pathPart || '';

        // Parse query parameters manually
        if (queryPart) {
          queryParams = {};
          queryPart.split('&').forEach(param => {
            const [key, value] = param.split('=');
            if (key && value) {
              queryParams[key] = decodeURIComponent(value);
            }
          });
        }
      } else if (url.includes('reset-password')) {
        // Web URL
        const urlObj = new URL(url);
        path = 'reset-password';
        queryParams = { token: urlObj.searchParams.get('token') };
      }

      if (path === 'reset-password' && queryParams?.token) {
        console.log('Opening reset password screen with token:', queryParams.token);
        setResetToken(queryParams.token as string);
        setCurrentScreen('reset-password');
        return true; // Indicate that we handled a deep link
      }
      return false;
    };

    // Get initial URL if app was opened via deep link
    const initialUrl = await Linking.getInitialURL();
    if (initialUrl) {
      const handled = handleUrl(initialUrl);
      if (handled) {
        return true;
      }
    }

    // Listen for deep links when app is running
    Linking.addEventListener('url', ({ url }) => {
      handleUrl(url);
    });

    return false;
  };

  const initializeApp = async () => {
    try {
      // Initialize auth service
      await authService.initialize();

      // Medir tiempo de inicio
      const start = Date.now();
      // Check if user is already authenticated (with token validation)
      const isAuthenticated = await authService.isAuthenticated();
      // Calcular cuánto tiempo ha pasado
      const elapsed = Date.now() - start;
      // Esperar el tiempo restante hasta 1.5 segundos
      if (elapsed < 2000) {
        await new Promise(res => setTimeout(res, 2000 - elapsed));
      }
      if (isAuthenticated) {
        await loadNavbarUserData();
        await loadUserPreferences();
        await loadNotifications();
        await loadNotificationPreferences();
        setCurrentScreen('main');
      } else {
        setCurrentScreen('welcome');
      }
    } catch (error) {
      console.error('Error initializing app:', error);
      setCurrentScreen('welcome');
    } finally {
      setIsInitialized(true);
    }
  };

  const handleSplashComplete = async () => {
    if (isInitialized) {
      try {
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
          await loadNavbarUserData();
          await loadUserPreferences();
          await loadNotifications();
          await loadNotificationPreferences();
          setCurrentScreen('main');
        } else {
          setCurrentScreen('welcome');
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        setCurrentScreen('welcome');
      }
    }
  };

  const handleWelcomeLoginPress = () => {
    setCurrentScreen('login');
  };

  const handleLoginBackPress = () => {
    setCurrentScreen('welcome');
  };

  const handleForgotPasswordPress = () => {
    setCurrentScreen('forgot-password');
  };

  const handleForgotPasswordBackPress = () => {
    setCurrentScreen('login');
  };

  const handleForgotPasswordSuccess = () => {
    setCurrentScreen('login');
  };

  const handleResetPasswordBackPress = () => {
    setCurrentScreen('login');
  };

  const handleResetPasswordSuccess = () => {
    setCurrentScreen('login');
  };

  const handleLoginSuccess = async () => {
    console.log('🔐 Login success - navigating to main screen');
    setOwnershipScopeAvailabilityLoaded(false);
    await loadNavbarUserData();
    await loadUserPreferences();
    await loadNotifications();
    await loadNotificationPreferences();
    setCurrentScreen('main');
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setOwnershipScopeAvailabilityLoaded(false);
      setShowOwnershipScopeToggle(true);
      setNavbarUserData({
        name: 'User',
        email: '',
        avatarUrl: null,
      });
      setNotifications([]);
      setNotificationPreferences(EMPTY_NOTIFICATION_PREFERENCES);
      setCurrentScreen('welcome');
    } catch (error) {
      console.error('Logout error:', error);
      setOwnershipScopeAvailabilityLoaded(false);
      setShowOwnershipScopeToggle(true);
      setNavbarUserData({
        name: 'User',
        email: '',
        avatarUrl: null,
      });
      setNotifications([]);
      setNotificationPreferences(EMPTY_NOTIFICATION_PREFERENCES);
      setCurrentScreen('welcome');
    }
  };

  const loadNavbarUserData = async () => {
    try {
      const profile = await authService.getUserProfile();
      const fallbackUser = authService.getCurrentUser();
      const userSource = profile || fallbackUser;

      const userName =
        typeof userSource?.name === 'string' && userSource.name.trim().length > 0
          ? userSource.name.trim()
          : typeof userSource?.email === 'string' && userSource.email.trim().length > 0
            ? userSource.email.trim()
            : 'User';

      const userEmail =
        typeof userSource?.email === 'string' && userSource.email.trim().length > 0
          ? userSource.email.trim()
          : '';

      const avatarUrl =
        typeof userSource?.profileImage === 'string' && userSource.profileImage.trim().length > 0
          ? userSource.profileImage.trim()
          : typeof userSource?.avatarUrl === 'string' && userSource.avatarUrl.trim().length > 0
            ? userSource.avatarUrl.trim()
            : null;

      setNavbarUserData({
        name: userName,
        email: userEmail,
        avatarUrl,
      });
    } catch (error) {
      console.error('Error loading navbar user data:', error);
    }
  };

  const handleLanguageChange = useCallback(
    async (_language: AppLanguage) => {
      const previousLanguage = appLanguage;
      // TEMP: force fixed language in demo
      // setAppLanguage(language);
      setAppLanguage('en');
      try {
        await apiService.updateUserPreferences({ language: 'en' });
      } catch (error) {
        setAppLanguage(previousLanguage);
        console.error('Error updating language preference:', error);
      }
    },
    [appLanguage]
  );

  const handleThemeChange = useCallback(
    async (_theme: AppTheme) => {
      const previousTheme = appTheme;
      // TEMP: force fixed light theme in demo
      // setAppTheme(theme);
      setAppTheme('light');
      try {
        await apiService.updateUserPreferences({ theme: 'light' });
      } catch (error) {
        setAppTheme(previousTheme);
        console.error('Error updating theme preference:', error);
      }
    },
    [appTheme]
  );

  const handlePhotoChange = useCallback(
    async (payload: { uri: string; mimeType: string }) => {
      const updatedPreferences = await apiService.uploadUserAvatar({
        uri: payload.uri,
        mimeType: payload.mimeType,
      });

      const freshAvatarUrl = updatedPreferences.avatarUrl
        ? `${updatedPreferences.avatarUrl}${updatedPreferences.avatarUrl.includes('?') ? '&' : '?'}v=${Date.now()}`
        : null;

      setNavbarUserData(prev => ({
        ...prev,
        avatarUrl: freshAvatarUrl,
      }));

      await loadNavbarUserData();
      await loadUserPreferences();
    },
    [loadNavbarUserData, loadUserPreferences]
  );

  const handlePhotoRemove = useCallback(async () => {
    await apiService.deleteUserAvatar();
    setNavbarUserData(prev => ({
      ...prev,
      avatarUrl: null,
    }));

    await loadNavbarUserData();
    await loadUserPreferences();
  }, [loadNavbarUserData, loadUserPreferences]);

  useEffect(() => {
    const shouldLoadAvailability =
      (currentScreen === 'main' || currentScreen === 'products' || currentScreen === 'events') &&
      !ownershipScopeAvailabilityLoaded;

    if (!shouldLoadAvailability) {
      return;
    }

    const loadOwnershipScopeAvailability = async () => {
      try {
        const response = await apiService.getOwnershipScopeAvailability();
        setShowOwnershipScopeToggle(response.canUseOwnershipScope);
      } catch (error) {
        console.error('Error loading ownership scope availability:', error);
        setShowOwnershipScopeToggle(true);
      } finally {
        setOwnershipScopeAvailabilityLoaded(true);
      }
    };

    loadOwnershipScopeAvailability();
  }, [currentScreen, ownershipScopeAvailabilityLoaded]);

  const handleNavigateToProducts = () => {
    setCurrentScreen('products');
    setSelectedProductIsin(null);
    setProductDetailFromReport(false);
  };

  const handleNavigateToEvents = () => {
    setCurrentScreen('events');
  };

  const handleNavigateToAnalytics = () => {
    setCurrentScreen('analytics');
  };

  const handleNavigateToFilteredProducts = (filters: {
    currency?: string;
    issuer?: string;
    maturityQuarter?: string;
    underlying?: string;
    eventType?: string;
    weekStart?: string;
    eventDate?: string;
    eventBucket?: 'nextWeek' | 'nextMaturity';
    nearBarrier?: boolean;
    riskType?: 'barrier' | 'barrierHit' | 'lowStrike';
    /** 1–99 = filtrar por distancia %; omitir o 100 = sin tope (solo barrier / strike monitor) */
    maxDistancePct?: number;
    productTypes?: string[];
    issuers?: string[];
    reportIds?: string[];
    selectedProductTypes?: string[];
    selectedIssuers?: string[];
    ownershipScope?: 'group' | 'self';
    includeWarrants?: boolean;
  }) => {
    setProductFilters({
      ...filters,
      ownershipScope: filters.ownershipScope || ownershipScope,
      includeWarrants: filters.includeWarrants,
    });
    setCurrentScreen('products');
  };

  const handleBackToMain = () => {
    setCurrentScreen('main');
    // Los filtros se mantienen en productFilters para persistir al volver a Products
  };

  const handleProductPress = (
    isin: string,
    returnScreen: 'main' | 'products' | 'events' = 'products',
    fromCustomReport = false
  ) => {
    setSelectedProductIsin(isin);
    setProductDetailReturnScreen(returnScreen);
    setProductDetailFromReport(fromCustomReport);
    setCurrentScreen('product-detail');
  };

  const handleBackFromProductDetail = () => {
    setCurrentScreen(productDetailReturnScreen);
    setSelectedProductIsin(null);
    setProductDetailFromReport(false);
    // productFilters is preserved when returning to products
  };

  const renderScreen = () => {
    console.log('🔄 Rendering screen:', currentScreen);
    switch (currentScreen) {
      case 'shared-product':
        return shareToken ? <SharedProductScreen token={shareToken} /> : null;
      case 'splash':
        return <SplashScreen onAnimationComplete={handleSplashComplete} />;
      case 'welcome':
        return <WelcomeScreen onLoginPress={handleWelcomeLoginPress} />;
      case 'login':
        return (
          <LoginScreen
            onBackPress={handleLoginBackPress}
            onLoginSuccess={handleLoginSuccess}
            onForgotPasswordPress={handleForgotPasswordPress}
          />
        );
      case 'forgot-password':
        return (
          <ForgotPasswordScreen
            onBackPress={handleForgotPasswordBackPress}
            onSuccess={handleForgotPasswordSuccess}
          />
        );
      case 'reset-password':
        return resetToken ? (
          <ResetPasswordScreen
            token={resetToken}
            onBackPress={handleResetPasswordBackPress}
            onSuccess={handleResetPasswordSuccess}
          />
        ) : null;
      case 'main':
      case 'products':
      case 'product-detail':
      case 'events':
      case 'analytics':
      case 'users':
      case 'settings':
      case 'notifications':
        // Pantallas principales con sidebar
        const screenMap = {
          main: 'Home',
          products: 'Products',
          'product-detail': 'Products',
          events: 'Events',
          analytics: 'Analytics',
          users: 'Portfolio',
          settings: 'Home',
          notifications: 'Home',
        };

        return (
          <View
            style={[styles.mainContainer, { backgroundColor: appearance.home.pageBackground }]}
            key={`app-${sessionEpoch}`}
          >
            {/* Sidebar - En desktop y tablet */}
            {(isDesktop || isTablet) && (
              <Sidebar
                theme={appTheme}
                isOpen={sidebarOpen}
                onToggle={setSidebarOpen}
                isCollapsed={sidebarCollapsed}
                onCollapsedChange={setSidebarCollapsed}
                currentScreen={screenMap[currentScreen]}
                onNavigateToHome={handleBackToMain}
                onNavigateToProducts={handleNavigateToProducts}
                onNavigateToEvents={handleNavigateToEvents}
                onNavigateToAnalytics={handleNavigateToAnalytics}
                onLogout={handleLogout}
              />
            )}

            {/* Hamburger button cuando sidebar está cerrado (solo en desktop) */}
            {!sidebarOpen && isDesktop && <HamburgerButton onPress={() => setSidebarOpen(true)} />}

            {/* Contenido principal */}
            <View
              style={[
                styles.content,
                { backgroundColor: appearance.home.pageBackground },
                sidebarOpen &&
                  isDesktop && {
                    marginLeft: sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
                  },
              ]}
            >
              <View
                style={[
                  styles.topBar,
                  {
                    backgroundColor: appearance.header.background,
                    borderBottomColor: appearance.header.border,
                  },
                ]}
              >
                <View />
                <TopBarProfile
                  userName={navbarUserData.name}
                  avatarUrl={navbarUserData.avatarUrl}
                  language={appLanguage}
                  theme={appTheme}
                  onSettingsPress={() => setCurrentScreen('settings')}
                  onLanguageChange={handleLanguageChange}
                  onThemeChange={handleThemeChange}
                  notifications={notifications}
                  unreadNotificationsCount={unreadNotificationsCount}
                  onNotificationPress={handleNotificationPress}
                  onGoToNotificationsPress={() => setCurrentScreen('notifications')}
                  onEditNotificationsPress={() => setCurrentScreen('settings')}
                  onMarkAllNotificationsAsRead={handleMarkAllNotificationsAsRead}
                  onLogoutPress={handleLogout}
                />
              </View>
              <ImpersonationBar
                onSessionChanged={() => {
                  setSessionEpoch(e => e + 1);
                  setNotifications([]);
                  setNotificationPreferences(EMPTY_NOTIFICATION_PREFERENCES);
                  setCurrentScreen('main');
                  void (async () => {
                    await loadNavbarUserData();
                    await loadUserPreferences();
                    await loadNotifications();
                    await loadNotificationPreferences();
                  })();
                }}
              />
              {currentScreen === 'main' && (
                <HomeScreen
                  theme={appTheme}
                  onLogout={handleLogout}
                  onNavigateToProducts={handleNavigateToProducts}
                  onNavigateToEvents={handleNavigateToEvents}
                  onNavigateToFilteredProducts={handleNavigateToFilteredProducts}
                  onProductPress={(isin) => handleProductPress(isin, 'main')}
                  notifications={notifications}
                  onNotificationPress={handleNotificationPress}
                  onGoToNotifications={() => setCurrentScreen('notifications')}
                  onNotificationsRefresh={loadNotifications}
                  ownershipScope={ownershipScope}
                  onOwnershipScopeChange={setOwnershipScope}
                  showOwnershipScopeToggle={showOwnershipScopeToggle}
                  sidebarOpen={sidebarOpen}
                  sidebarCollapsed={sidebarCollapsed}
                />
              )}
              {currentScreen === 'products' && (
                <ProductsScreen
                  theme={appTheme}
                  onBackPress={handleBackToMain}
                  onNavigateToProducts={handleNavigateToProducts}
                  onNavigateToHome={handleBackToMain}
                  onProductPress={(isin, fromCustomReport) =>
                    handleProductPress(isin, 'products', fromCustomReport)
                  }
                  onFiltersChange={setProductFilters}
                  initialFilters={productFilters}
                  ownershipScope={ownershipScope}
                  onOwnershipScopeChange={setOwnershipScope}
                  showOwnershipScopeToggle={showOwnershipScopeToggle}
                />
              )}
              {currentScreen === 'product-detail' && selectedProductIsin && (
                <ProductDetailScreen
                  theme={appTheme}
                  isin={selectedProductIsin}
                  onBackPress={handleBackFromProductDetail}
                  onNavigateToProducts={handleNavigateToProducts}
                  onNavigateToHome={handleBackToMain}
                  ownershipScope={ownershipScope}
                  fromCustomReport={productDetailFromReport}
                />
              )}
              {currentScreen === 'events' && (
                <EventsScreen
                  theme={appTheme}
                  navigation={{ goBack: handleBackToMain }}
                  onNavigateToFilteredProducts={handleNavigateToFilteredProducts}
                  onProductPress={(isin) => handleProductPress(isin, 'events')}
                  ownershipScope={ownershipScope}
                  onOwnershipScopeChange={setOwnershipScope}
                  showOwnershipScopeToggle={showOwnershipScopeToggle}
                />
              )}
              {currentScreen === 'analytics' && (
                <UserAnalyticsAdminScreen
                  onNavigateToProducts={handleNavigateToProducts}
                  onNavigateToHome={handleBackToMain}
                  onNavigateToEvents={handleNavigateToEvents}
                />
              )}
              {currentScreen === 'settings' && (
                <SettingsScreen
                  displayName={navbarUserData.name}
                  email={navbarUserData.email}
                  avatarUrl={navbarUserData.avatarUrl}
                  language={appLanguage}
                  theme={appTheme}
                  onLanguageChange={handleLanguageChange}
                  onThemeChange={handleThemeChange}
                  onPhotoChange={handlePhotoChange}
                  onPhotoRemove={handlePhotoRemove}
                  notificationPreferences={notificationPreferences}
                  onNotificationPreferencesChange={handleNotificationPreferencesChange}
                />
              )}
              {currentScreen === 'notifications' && (
                <NotificationsScreen
                  notifications={notifications}
                  onNotificationPress={handleNotificationPress}
                  onMarkAllAsRead={handleMarkAllNotificationsAsRead}
                  onEditNotifications={() => setCurrentScreen('settings')}
                />
              )}

              {/* Bottom Navigation */}
              <BottomNavigation
                activeTab={
                  currentScreen === 'main'
                    ? 'home'
                    : currentScreen === 'products'
                      ? 'portfolio'
                      : currentScreen === 'events'
                        ? 'events'
                        : currentScreen === 'analytics'
                          ? 'more'
                          : 'home'
                }
                onNavigateToHome={handleBackToMain}
                onNavigateToProducts={handleNavigateToProducts}
                onNavigateToEvents={handleNavigateToEvents}
                showAnalyticsTab={authService.canAccessAdminAnalytics()}
                onNavigateToAnalytics={handleNavigateToAnalytics}
                onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
              />
            </View>
          </View>
        );
      default:
        return <SplashScreen onAnimationComplete={handleSplashComplete} />;
    }
  };

  return <View style={styles.container}>{renderScreen()}</View>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  mainContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  content: {
    flex: 1,
    // @ts-ignore - transition is valid for web
    transition: 'margin-left 0.3s ease',
  },
  topBar: {
    minHeight: 64,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingHorizontal: 24,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    zIndex: 2000,
    elevation: 10,
    ...Platform.select({
      web: {
        // @ts-ignore - valid web style
        position: 'relative',
        // @ts-ignore - valid web style
        overflow: 'visible',
      },
      default: {},
    }),
  },
  // Estos estilos ya no se usan, el marginLeft se aplica inline
  contentWithSidebar: {},
  contentWithSidebarCollapsed: {},
});
